import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy
} from 'firebase/firestore';
import {
  getDifficultyLabel,
  getRecruitmentStatusLabel,
  getActivityFrequencyLabel,
  getAgeGroupLabel,
  getActivityStyleLabel
} from '../utils/recruitmentDisplay';
import './ApplicationHistory.css';

function getCreatedAtTimestamp(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string' || typeof value === 'number') return new Date(value).getTime() || 0;
  return 0;
}

function sortRecruitmentsByCreatedAtDesc(items) {
  return [...items].sort((a, b) => getCreatedAtTimestamp(b.createdAt) - getCreatedAtTimestamp(a.createdAt));
}

const TABS = [
  { key: 'single', label: '単発募集' },
  { key: 'group', label: '所属メンバー募集' }
];

function MyRecruitments() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('single');
  const [myRecruitments, setMyRecruitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMyRecruitments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const myRecruitmentsQuery = query(
        collection(db, 'recruitments'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(myRecruitmentsQuery);

      const recruitmentsData = await Promise.all(
        snapshot.docs.map(async (snap) => {
          const recruitment = { id: snap.id, ...snap.data() };

          const applicationsQuery = query(
            collection(db, 'applications'),
            where('recruitmentId', '==', snap.id)
          );
          const applicationsSnapshot = await getDocs(applicationsQuery);

          return {
            ...recruitment,
            applicationCount: applicationsSnapshot.size,
            applicants: applicationsSnapshot.docs.map(a => ({ id: a.id, ...a.data() }))
          };
        })
      );

      setMyRecruitments(sortRecruitmentsByCreatedAtDesc(recruitmentsData));
      if (recruitmentsData.length > 0) {
        setActiveTab(recruitmentsData[0].recruitmentType === 'group' ? 'group' : 'single');
      }
    } catch (err) {
      console.error('募集履歴の取得に失敗しました:', err);
      setError('募集履歴の取得に失敗しました。時間をおいて再試行してください。');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchMyRecruitments();
    }
  }, [currentUser, fetchMyRecruitments]);

  if (loading) {
    return <div className="loading">読み込み中...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const filteredRecruitments = myRecruitments.filter(r => {
    if (activeTab === 'single') return r.recruitmentType === 'single' || !r.recruitmentType;
    return r.recruitmentType === 'group';
  });

  return (
    <div className="application-history-container">
      <h1>募集履歴</h1>

      <div className="tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label} ({myRecruitments.filter(r =>
              tab.key === 'single'
                ? (r.recruitmentType === 'single' || !r.recruitmentType)
                : r.recruitmentType === 'group'
            ).length})
          </button>
        ))}
      </div>

      <div className="tab-content">
        <div className="recruitments-list">
          {filteredRecruitments.length > 0 ? (
            filteredRecruitments.map((recruitment) => (
              <div key={recruitment.id} className="recruitment-card">
                <div className="card-header">
                  <h3>{recruitment.mountainName || recruitment.groupName}</h3>
                  {recruitment.difficulty && (
                    <span className={`difficulty ${recruitment.difficulty}`}>
                      {getDifficultyLabel(recruitment.difficulty)}
                    </span>
                  )}
                </div>

                <div className="card-body">
                  {recruitment.recruitmentType !== 'group' && (
                    <p className="route">ルート: {recruitment.route || '未設定'}</p>
                  )}
                  {recruitment.recruitmentType !== 'group' && recruitment.departureDate && (
                    <p className="date">
                      📅 {recruitment.departureDate?.toDate
                        ? recruitment.departureDate.toDate().toLocaleDateString()
                        : new Date(recruitment.departureDate).toLocaleDateString()}
                    </p>
                  )}
                  {recruitment.recruitmentType === 'group' && recruitment.activityFrequency && (
                    <p className="route">
                      活動頻度: {getActivityFrequencyLabel(recruitment.activityFrequency, recruitment.activityFrequencyOther)}
                    </p>
                  )}
                  {recruitment.recruitmentType === 'group' && recruitment.genderRatio && (
                    <p className="route">男女比: {recruitment.genderRatio}</p>
                  )}
                  {recruitment.recruitmentType === 'group' && recruitment.ageGroup && (
                    <p className="route">年齢層: {getAgeGroupLabel(recruitment.ageGroup)}</p>
                  )}
                  {recruitment.recruitmentType === 'group' && recruitment.activityStyle && (
                    <p className="route">活動スタイル: {getActivityStyleLabel(recruitment.activityStyle)}</p>
                  )}
                  {recruitment.recruitmentType === 'group' ? (
                    <p className="participants">👥 応募数: {recruitment.applicationCount}件</p>
                  ) : (
                    <p className="participants">👥 {recruitment.applicationCount}/{recruitment.maxParticipants}人</p>
                  )}
                  <p className={`status ${recruitment.status}`}>
                    {getRecruitmentStatusLabel(recruitment.status)}
                  </p>

                  {recruitment.applicants.length > 0 && (
                    <div className="applicants-section">
                      <h4>応募者一覧</h4>
                      <div className="applicants-list">
                        {recruitment.applicants.map((applicant) => {
                          const appStatus = applicant.applicationStatus || 'pending';
                          const statusLabels = { pending: '承認待ち', approved: '承認済み', rejected: '拒否済み' };
                          const statusClasses = { pending: 'app-status-pending', approved: 'app-status-approved', rejected: 'app-status-rejected' };
                          return (
                            <div key={applicant.id} className="applicant-item">
                              <span className="applicant-name">
                                {applicant.applicant?.nickname || '不明'}
                              </span>
                              <span className={`app-status-badge ${statusClasses[appStatus]}`}>
                                {statusLabels[appStatus]}
                              </span>
                              <span className="apply-date">
                                {applicant.createdAt?.toDate
                                  ? applicant.createdAt.toDate().toLocaleDateString()
                                  : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="card-footer">
                  <Link
                    to={`/recruitments/${recruitment.id}`}
                    className="btn btn-outline"
                  >
                    詳細を見る
                  </Link>
                  {recruitment.recruitmentType === 'group' && (
                    <Link
                      to={`/circles/${recruitment.id}/manage`}
                      className="btn btn-outline"
                    >
                      管理画面
                    </Link>
                  )}
                  <Link
                    to={`/edit-recruitment/${recruitment.id}`}
                    className="btn btn-primary"
                  >
                    編集する
                  </Link>
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">
              <p>
                {activeTab === 'single' ? '単発募集' : '所属メンバー募集'}の履歴がありません。
              </p>
              <Link to="/create-recruitment" className="btn btn-primary">
                募集を作成する
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyRecruitments;
